import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  STAGES,
  stageProgressOf,
  tokenVehicleForStage,
  nodeIconFor,
  segmentAtProgress,
  stageAtProgress,
} from "./stages";
import "./TrackLifecycle.css";

/**
 * The infinity road — EXACT centre-line traced from the Sendtoo logo's "oo"
 * symbol (coordinate box ~596 x 282, viewBox "-50 -40 696 362"). Verbatim —
 * do not edit numbers, do not re-draw or smooth. It is an OPEN road: starts at
 * the top of the left loop, rounds it, rises through the centre, rounds the
 * right loop and ends at its bottom. The journey never wraps back to the start.
 */
const JOURNEY_PATH =
  "M231.0,128.8 C230.9,121.3 236.1,96.8 230.9,83.8 C225.6,70.9 212.2,58.8 199.6,51.3 C187.1,43.9 170.5,40.1 155.6,39.2 C140.8,38.3 124.5,40.9 110.5,46.0 C96.5,51.2 82.5,59.9 71.7,70.2 C60.9,80.4 50.9,93.8 45.6,107.6 C40.2,121.3 37.8,138.0 39.5,152.6 C41.2,167.2 47.6,182.9 55.9,195.2 C64.2,207.4 76.6,218.5 89.4,226.0 C102.3,233.6 118.0,238.3 132.8,240.6 C147.7,242.8 163.8,242.3 178.5,239.5 C193.3,236.6 208.0,230.4 221.4,223.2 C234.7,216.0 247.0,206.3 258.6,196.3 C270.1,186.4 280.5,174.9 290.6,163.4 C300.7,151.9 309.7,139.4 319.3,127.5 C329.0,115.6 337.9,103.0 348.4,91.9 C358.9,80.8 369.6,68.9 382.3,60.9 C395.0,52.9 409.9,46.1 424.5,43.8 C439.1,41.4 455.5,43.3 470.1,46.9 C484.7,50.5 499.7,56.8 512.0,65.3 C524.4,73.7 537.1,84.8 544.3,97.5 C551.5,110.2 555.5,126.8 555.3,141.4 C555.0,156.0 549.6,172.1 542.7,185.3 C535.9,198.5 525.5,211.5 514.0,220.8 C502.4,230.1 487.6,237.7 473.2,241.1 C458.8,244.5 442.0,244.5 427.5,241.3 C413.1,238.1 396.5,231.9 386.3,222.0 C376.2,212.1 370.1,188.6 366.8,181.9";

/* viewBox coordinate frame: road occupies ~x 0-596, y 0-282; margin is for labels */
const VB = { x: -50, y: -40, w: 696, h: 362 };

const IDLE_LOOP_MS = 16000; // calm full demo run, start of road to end of road
const IDLE_DWELL_MS = 1600; // dwell at "Delivered" before the demo restarts (no wrap-around)
const ANIM_MS = 2500; // result reveal
const IDLE_LINEHAUL = ["rail", "air", "sea"]; // demo alternates train / plane / ship
const TRAIL_LEN = 0.045; // trail segment length as a fraction of the path

const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

/**
 * <TrackLifecycle /> — animated Sendtoo infinity shipment lifecycle.
 * Purely presentational; everything arrives via props.
 *
 * Props:
 *   stage     1-8 for a real shipment, null for the idle demo loop
 *   mode      "road" | "rail" | "air" | "sea" (stages 4 & 5 icons)
 *   state     "normal" | "delayed" | "exception" | "returned"
 *   events    optional [{ stage, label, city, time }] for node tooltips
 *   compact   smaller variant
 *   className
 */
export default function TrackLifecycle({
  stage = null,
  mode = "road",
  state = "normal",
  events = [],
  compact = false,
  className = "",
}) {
  const rootRef = useRef(null);
  const pathRef = useRef(null); // visible base road path (also the measuring stick)
  const progressRef = useRef(null);
  const progressGlowRef = useRef(null);
  const progressMaskRef = useRef(null);
  const trailRef = useRef(null);
  const tokenRef = useRef(null);

  const [geo, setGeo] = useState(null); // { len, pts: [{x,y} x8] }
  const [loopCount, setLoopCount] = useState(0);
  const [vehicleStage, setVehicleStage] = useState(1);
  const [reached, setReached] = useState(1); // highest stage reached at current p
  const [resting, setResting] = useState(false);
  const [activeTip, setActiveTip] = useState(null);
  const [inView, setInView] = useState(true);

  const prog = useRef({ p: 0, from: 0, to: 0, q: 0, phase: "idle" });
  const reduced = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );
  const maskId = `tl-pm-${useId().replace(/:/g, "")}`;
  const gradId = `tl-pg-${useId().replace(/:/g, "")}`;

  const idle = stage === null;
  const effectiveMode = idle ? IDLE_LINEHAUL[loopCount % IDLE_LINEHAUL.length] : mode;

  /* Measure the path once (user units — independent of rendered size). */
  useEffect(() => {
    if (!pathRef.current) return undefined;
    const len = pathRef.current.getTotalLength();
    const pts = STAGES.map((s) => {
      const pt = pathRef.current.getPointAtLength(s.p * len);
      return { x: pt.x, y: pt.y };
    });
    setGeo({ len, pts });
    return undefined;
  }, []);

  const applyFrame = useCallback(
    (p) => {
      if (!geo || !pathRef.current) return;
      progressRef.current?.setAttribute("stroke-dasharray", `${p} 1`);
      progressGlowRef.current?.setAttribute("stroke-dasharray", `${p} 1`);
      progressMaskRef.current?.setAttribute("stroke-dasharray", `${p} 1`);
      trailRef.current?.setAttribute("stroke-dashoffset", `${TRAIL_LEN - p}`);
      const pt = pathRef.current.getPointAtLength(p * geo.len);
      tokenRef.current?.setAttribute("transform", `translate(${pt.x} ${pt.y})`);
      const seg = segmentAtProgress(p);
      setVehicleStage((prev) => (prev === seg ? prev : seg));
      const r = stageAtProgress(p);
      setReached((prev) => (prev === r ? prev : r));
    },
    [geo]
  );

  /* React to prop changes: pick the animation target. */
  useEffect(() => {
    if (!geo) return;
    const pr = prog.current;
    if (idle) {
      pr.phase = "idle";
      return;
    }
    const target = state === "returned" ? 0 : stageProgressOf(stage);
    if (reduced) {
      pr.p = target;
      pr.phase = "rest";
      setResting(true);
      applyFrame(target);
      return;
    }
    pr.phase = "anim";
    pr.from = pr.p;
    pr.to = target;
    pr.q = 0;
    setResting(false);
  }, [stage, state, idle, reduced, geo, applyFrame]);

  /* The rAF engine. Runs only when on-screen, tab visible, motion allowed. */
  useEffect(() => {
    if (!geo || reduced || !inView) return undefined;
    let last = performance.now();
    let raf = 0;
    const tick = (now) => {
      const dt = Math.min(now - last, 120);
      last = now;
      const pr = prog.current;
      if (pr.phase === "idle") {
        pr.p += dt / IDLE_LOOP_MS;
        if (pr.p >= 1) {
          pr.p = 1;
          pr.phase = "dwell"; // dwell at the end of the road, then restart — never wrap
          pr.dwellT = 0;
        }
      } else if (pr.phase === "dwell") {
        pr.dwellT += dt;
        if (pr.dwellT >= IDLE_DWELL_MS) {
          pr.p = 0;
          pr.phase = "idle";
          setLoopCount((l) => l + 1);
        }
      } else if (pr.phase === "anim") {
        pr.q += dt / ANIM_MS;
        if (pr.q >= 1) {
          pr.p = pr.to;
          pr.phase = "rest";
          setResting(true);
        } else {
          pr.p = pr.from + (pr.to - pr.from) * easeInOutCubic(pr.q);
        }
      }
      applyFrame(pr.p);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [geo, reduced, inView, applyFrame]);

  /* Pause when off-screen or tab hidden. */
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const el = rootRef.current;
    let io = null;
    if ("IntersectionObserver" in window && el) {
      io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.05 });
      io.observe(el);
    }
    const onVis = () => setInView(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVis);
    return () => {
      if (io) io.disconnect();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  /* Static final state under reduced motion / before first frame. */
  useEffect(() => {
    if (geo && reduced) applyFrame(prog.current.p);
  }, [geo, reduced, applyFrame]);

  /* ---- derived display values ---- */
  const currentStage = idle ? reached : stage;
  const currentInfo = STAGES[currentStage - 1];
  const TokenVehicle = tokenVehicleForStage(vehicleStage, effectiveMode);

  const nodeState = (n) => {
    if (idle) {
      if (n < reached) return "done";
      if (n === reached) return "current";
      return "future";
    }
    if (state === "returned") return n === 1 ? "current" : "future";
    if (n < stage) return "done";
    if (n === stage) return "current";
    return "future";
  };

  const caption = (() => {
    if (idle) return currentInfo.label;
    if (state === "returned") return "Returned to sender";
    if (state === "exception") return `${currentInfo.label} — needs attention`;
    if (state === "delayed") return `${currentInfo.label} — running late`;
    return currentInfo.label;
  })();

  const ariaText = idle
    ? `Sample shipment journey, showing stage ${currentStage} of 8: ${currentInfo.label}`
    : state === "returned"
      ? "Shipment returned to sender"
      : `Shipment at stage ${currentStage} of 8: ${currentInfo.label}`;

  const currentEvent = events.find((e) => e.stage === currentStage);

  return (
    <div
      ref={rootRef}
      className={`tl-loop ${compact ? "tl-compact" : ""} ${className}`}
      data-testid="track-lifecycle"
      data-state={state}
      data-complete={(idle ? reached === 8 : stage === 8 && state !== "returned") ? "true" : "false"}
    >
      <div
        className="tl-loop-stage"
        role="img"
        aria-label={ariaText}
        data-testid="tl-stage"
      >
        <svg
          className="tl-svg"
          viewBox={`${VB.x} ${VB.y} ${VB.w} ${VB.h}`}
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id={gradId} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="596" y2="0">
              <stop offset="0" stopColor="#0b63f6" />
              <stop offset="1" stopColor="#159dff" />
            </linearGradient>
            <mask id={maskId} maskUnits="userSpaceOnUse" x={VB.x} y={VB.y} width={VB.w} height={VB.h}>
              <path
                ref={progressMaskRef}
                d={JOURNEY_PATH}
                pathLength="1"
                fill="none"
                stroke="#ffffff"
                strokeWidth="88"
                strokeLinecap="butt"
                strokeDasharray="0 1"
              />
            </mask>
          </defs>
          {/* road: glow, border, body, dashed centre line */}
          <path ref={pathRef} d={JOURNEY_PATH} className="tl-road-glow" pathLength="1" />
          <path d={JOURNEY_PATH} className="tl-road-border" pathLength="1" />
          <path d={JOURNEY_PATH} className="tl-road-base" pathLength="1" />
          <path d={JOURNEY_PATH} className="tl-road-dash" />
          {/* completed part: bright blue gradient + glow */}
          <path ref={progressGlowRef} d={JOURNEY_PATH} className="tl-road-progress-glow" pathLength="1" strokeDasharray="0 1" />
          <path
            ref={progressRef}
            d={JOURNEY_PATH}
            className="tl-road-progress"
            pathLength="1"
            strokeDasharray="0 1"
            stroke={`url(#${gradId})`}
          />
          {/* bright centre dashes, masked to the completed part of the journey */}
          <path d={JOURNEY_PATH} className="tl-road-dash tl-road-progress-dash" mask={`url(#${maskId})`} />
          {/* soft trail behind the token */}
          <path
            ref={trailRef}
            d={JOURNEY_PATH}
            className="tl-trail"
            pathLength="1"
            strokeDasharray={`${TRAIL_LEN} ${1 - TRAIL_LEN}`}
            strokeDashoffset={TRAIL_LEN}
          />
          {/* parcel token (~52 units across, sits inside the 74-unit road) */}
          <g ref={tokenRef} className={`tl-token ${resting ? "tl-token-rest" : ""}`} data-testid="tl-token">
            <circle r="34" className="tl-token-pulse" />
            <circle r="26" className="tl-token-badge" />
            <g transform="translate(-14,-14)">
              <g key={`${vehicleStage}-${effectiveMode}`} className="tl-token-swap">
                <TokenVehicle size={28} color="#ffffff" strokeWidth={2} aria-hidden="true" />
              </g>
            </g>
          </g>
        </svg>

        {/* stage nodes (HTML overlay, keyboard-focusable) */}
        {geo &&
          STAGES.map((s, i) => {
            const ns = nodeState(s.n);
            const Icon = nodeIconFor(s.n, effectiveMode);
            const ev = events.find((e) => e.stage === s.n);
            const pos = geo.pts[i];
            return (
              <button
                key={s.key}
                type="button"
                className={`tl-node tl-node-${ns} tl-side-${s.side} ${
                  activeTip === s.n ? "tl-tip-open" : ""
                } ${s.n === 8 && currentStage === 8 && !idle ? "tl-delivered" : ""}`}
                style={{ left: `${((pos.x - VB.x) / VB.w) * 100}%`, top: `${((pos.y - VB.y) / VB.h) * 100}%` }}
                aria-label={`Stage ${s.n} of 8: ${s.label}${ns === "current" ? " (current)" : ns === "done" ? " (completed)" : ""}`}
                data-testid={`tl-node-${s.key}`}
                onClick={() => setActiveTip(activeTip === s.n ? null : s.n)}
                onBlur={() => setActiveTip((t) => (t === s.n ? null : t))}
              >
                <span className="tl-node-badge">
                  <Icon aria-hidden="true" />
                </span>
                <span className="tl-node-label">{s.label}</span>
                <span className="tl-tooltip" role="tooltip">
                  <span className="tl-tooltip-title">{s.label}</span>
                  <span className="tl-tooltip-desc">{s.desc}</span>
                  {ev && (ev.time || ev.city) && (
                    <span className="tl-tooltip-meta">{[ev.city, ev.time].filter(Boolean).join(" · ")}</span>
                  )}
                </span>
              </button>
            );
          })}
      </div>

      {/* caption + idle tag */}
      <div className={`tl-caption tl-caption-${state}`} data-testid="tl-caption">
        {idle && <span className="tl-tag">Sample journey</span>}
        <span className="tl-caption-text">{caption}</span>
      </div>

      {/* mobile: current stage card + compact stepper (no labels around nodes) */}
      <div className="tl-mobile-info">
        <div className="tl-mobile-card" data-testid="tl-mobile-card">
          <span className="tl-mobile-card-icon">
            {(() => {
              const MobileIcon = nodeIconFor(currentStage, effectiveMode);
              return <MobileIcon size={20} aria-hidden="true" />;
            })()}
          </span>
          <span>
            <span className="tl-mobile-card-title">{idle ? currentInfo.label : caption}</span>
            {currentEvent && (currentEvent.city || currentEvent.time) && (
              <span className="tl-mobile-card-meta">
                {[currentEvent.city, currentEvent.time].filter(Boolean).join(" · ")}
              </span>
            )}
          </span>
        </div>
        <ol className="tl-stepper" data-testid="tl-stepper">
          {STAGES.map((s) => {
            const ns = nodeState(s.n);
            const Icon = nodeIconFor(s.n, effectiveMode);
            return (
              <li key={s.key} className={`tl-step tl-step-${ns}`} aria-label={s.label} title={s.label}>
                <Icon size={16} aria-hidden="true" />
              </li>
            );
          })}
        </ol>
      </div>

      <span className="tl-sr" aria-live="polite">
        {ariaText}
      </span>
    </div>
  );
}
